import AWS from "aws-sdk";
import { Signer } from "aws-amplify"

interface WebSocketListener{
    key: string;
    handler: (data:any, args?:any[]) => void;
    extraArgs?: any[];
}

/**
 * How long waitUntilConnected will wait for the handshake before reporting the socket unreachable.
 *
 * Opening this socket crosses two unprovisioned Java Lambdas in series — the authorizer, then the $connect
 * handler — so a first connection after idle takes far longer than a WebSocket handshake normally suggests.
 * A timeout below that gives up mid-handshake and reports the server unreachable, discarding whatever the
 * operator had pasted. Must stay clear of COLD_START_HANDSHAKE_FLOOR_MS.
 */
export const CONNECT_TIMEOUT_MS = 30000;

/**
 * The slowest cold-start handshake this socket is known to take. CONNECT_TIMEOUT_MS has to exceed it, and it
 * sits beside it so that lowering the timeout below the floor is caught rather than left to surface at runtime.
 */
export const COLD_START_HANDSHAKE_FLOOR_MS = 11400;

/**
 * Base class for interactions with a WebSocket server
 */
export default class WebSocketApi {
    private internalConnectionCloseCode = 3456;
    public static ConnectionEvent = {
        ON_OPEN:"ON_OPEN",
        ON_CLOSE:"ON_CLOSE",
        ON_MESSAGE_SENT: "ON_MESSAGE_SENT",
        ON_MESSAGE_RECEIVED: "ON_MESSAGE_RECEIVED",
        ON_ERROR: "ON_ERROR",
    }
    /**
     * The actual WebSocket client
     * https://developer.mozilla.org/en-US/docs/Web/API/WebSocket
     */
    private socket?: WebSocket;

    /**
     * Simple flag to allow reconnecting
     */
    private connected: boolean;
    
    /**
     * Flag to know when a connection attempt is ongoing to avoid multiple connection attempts
     */
    private isConnecting: boolean;
    
    /**
     * Array to store server message listeners
     */
    private listeners: WebSocketListener[];

    /**
     * Utility array to store messages that couldn't be sent because the socket was not connected
     */
    private pendingMessages: string[];

    /**
     * How many waitUntilConnected calls are currently waiting on a handshake.
     *
     * Needed because ensureConnected returns early while one is in flight, so concurrent callers share a
     * socket rather than getting one each — and the first to time out must not close a socket another is
     * still awaiting. An identity check cannot tell a shared socket from a replaced one.
     */
    private pendingWaiters = 0;

    /**
     * The socket a waitUntilConnected call opened, if any.
     *
     * Recorded on the instance rather than per call, so whichever waiter is LAST out can abandon it. Held as a
     * per-call flag, two concurrent waiters sharing one socket wedged it permanently: the creator's flag was
     * true but the other waiter happened to be the one that dropped the count to zero, so the abandon saw
     * false, bailed on a socket that was still CONNECTING, and left it in the field forever — the exact
     * dead-end this mechanism exists to prevent.
     *
     * Still narrower than "any socket in the field": a handshake one of the nine fire-and-forget consumers
     * started never lands here, so their in-flight connection cannot be closed by a bulk timeout.
     */
    private socketOpenedByWaiter?: WebSocket;

    private authToken: string;
    
    constructor(authToken: string){
        this.socket = undefined;
        this.listeners = [];
        this.pendingMessages = [];
        this.connected = false;
        this.isConnecting = false;
        this.authToken = authToken;
    }

    connect(url: string) {
        if (this.socket) {
            this.socket.close(this.internalConnectionCloseCode);
            return;
        }
        this.isConnecting = true;                 
        const credentials: any = (AWS.config.credentials as any)?.data?.Credentials || {};
        this.socket = new WebSocket(`${url}?idToken=${this.authToken}`);
        const self = this;

        this.socket.onopen = function () {
            self.connected = true;
            self.isConnecting = false;
            // Send any pending messages upon successful connection
            self.pendingMessages.forEach(m => self._sendMessage(m));
            self.pendingMessages = [];
            self.listeners.forEach(l => {
                if (l.key == WebSocketApi.ConnectionEvent.ON_OPEN){
                    // Notify listerners
                    l.handler(new Date().toISOString(), l.extraArgs);
                }
            });
        };
        this.socket.onerror = function(ev: Event){
            self.listeners.forEach(l => {
                if (l.key === WebSocketApi.ConnectionEvent.ON_ERROR){
                    l.handler(ev, l.extraArgs);
                    self.connected = false;
                    self.isConnecting = false;
                    self.socket = undefined;
                }
            });
        }

        // Attach onMessage listener
        this.socket.onmessage = function (e:MessageEvent) {
            self.listeners.forEach(l => {
                if (l.key === '' || l.key === e.data.key || l.key == WebSocketApi.ConnectionEvent.ON_MESSAGE_RECEIVED){
                    // Notify listerners
                    l.handler(e.data, l.extraArgs);
                }
            });
        };


        this.socket.onclose = function (ev: CloseEvent) {
            self.connected = false;
            self.isConnecting = false;
            if (ev.code == self.internalConnectionCloseCode){
                //This event should be ignored
                return;
            }
            console.log('****** WS: Connection is closed...',ev);
            self.listeners.forEach(l => {
                if (l.key == WebSocketApi.ConnectionEvent.ON_CLOSE){
                    // Notify listerners
                    l.handler(new Date().toISOString(), l.extraArgs);
                }
            });
        };
    }

    /**
     * Checks if client is connected
     * @returns True if connected
     */
    isConnected(){
        return this.connected;
    }

    /**
     * Makes sure the client is connected
     * @param url The Web Socket API URL
     */
    ensureConnected(url: string) {
        if (this.socket != null && (this.isConnecting || this.isConnected())){
            return;
        }
        this.connect(url);
    }

    /**
     * Connects if needed and resolves once the socket is actually open.
     *
     * New, and called only from the bulk promotion screen. ensureConnected() only *starts* a connection, so a
     * send issued straight after it sees readyState CONNECTING and gets queued — and a caller that treats
     * "not sent yet" as "not sent" then tells the operator nothing was submitted while a batch is in fact
     * being created. Awaiting this first removes the ambiguity.
     *
     * <p>Deliberately does NOT repair the shared reconnect behaviour. connect() returns early while a socket
     * is still in the field and onclose leaves it there, so after API Gateway's ten-minute idle close the
     * first call here cannot build a socket and waits out the timeout. The abandon on that path then drops the
     * dead socket, so a second attempt connects. Fixing the shared handlers changes runtime behaviour for the
     * ten existing socket consumers, which is being taken separately rather than carried by this feature.
     *
     * @returns true if the socket is open, false if the handshake failed or did not finish in time
     */
    async waitUntilConnected(url: string, timeoutMs = CONNECT_TIMEOUT_MS): Promise<boolean> {
        let waitingOn: WebSocket | undefined;
        try {
            /*
             * Noted before connecting so the timeout can tell a socket THIS call opened from one it merely
             * found. The client is a shared singleton and the other nine consumers open it fire-and-forget
             * through ensureConnected — balance pull, bulk reversal, period charges export, CLI execution —
             * so a socket already in flight may belong to one of them, and closing it on this call's timeout
             * would break a feature that never asked to wait.
             */
            const before = this.socket;
            this.ensureConnected(url);
            waitingOn = this.socket;
            if (waitingOn !== undefined && waitingOn !== before) {
                this.socketOpenedByWaiter = waitingOn;
            }
        } catch {
            // The WebSocket constructor throws synchronously on a malformed url — which this one can be,
            // since the value is the raw Katal placeholder until the stage substitutes it. Resolving false
            // rather than rejecting is what this method documents, and a caller written to that contract
            // otherwise gets an unhandled rejection where "server unreachable" belonged.
            return false;
        }
        if (this.isConnected()) {
            return true;
        }
        /*
         * Counted, because the identity check in abandonPendingConnection is not sufficient on its own: it
         * catches a socket that has been REPLACED, not one that is SHARED. ensureConnected returns early while
         * a handshake is in flight, so a second concurrent caller captures the very same socket as waitingOn —
         * and the first caller's timeout would then close the socket the second is still legitimately awaiting,
         * reporting a batch as unsubmitted while its handshake was progressing. Only the last waiter out
         * abandons.
         */
        this.pendingWaiters += 1;
        try {
            const startedAt = Date.now();
            // Polled rather than hooked onto onopen, because onopen is already owned by connect() and adding a
            // second assignment there would silently replace the pending-message flush.
            while (Date.now() - startedAt < timeoutMs) {
                await new Promise(resolve => setTimeout(resolve, 50));
                if (this.isConnected()) {
                    return true;
                }
                // onerror clears the socket when a listener is registered, so this is a handshake that has
                // definitively failed rather than one still in progress. The timeout is sized for a cold
                // start, so waiting it out would stall the caller long after the answer is known.
                if (this.socket == null && !this.isConnecting) {
                    return false;
                }
            }
        } finally {
            this.pendingWaiters -= 1;
        }
        // Decremented above first, so this reads "nobody else is still waiting on it".
        if (this.pendingWaiters === 0) {
            this.abandonPendingConnection(waitingOn);
        }
        return false;
    }

    /**
     * Gives up on a handshake that has not completed, leaving this client able to try again.
     *
     * Reached only from waitUntilConnected, so only the bulk promotion screen runs it. Without it a single
     * timeout was permanent: connect() had set isConnecting and left the socket in the field, no handler had
     * fired to reset either, and every later ensureConnected() then took its early return for the life of
     * the page.
     *
     * Guarded three ways, each for a case the others miss:
     *
     * <ul>
     * <li>identity, so it cannot close a socket that has REPLACED the one the caller was waiting on;
     * <li>a waiter count, because concurrent callers SHARE one socket and identity cannot tell that from a
     *     replacement — only the last waiter out gets here;
     * <li>provenance, so a handshake one of the nine fire-and-forget consumers started is left alone. They
     *     reach the socket through ensureConnected and are not represented in the count, so closing their
     *     in-flight connection would strand a balance pull or a journal reversal that never asked to wait.
     * </ul>
     */
    private abandonPendingConnection(ws?: WebSocket) {
        if (ws === undefined || this.socket !== ws) {
            return;
        }
        /*
         * Provenance is read off the instance, not off the calling frame. Whether the caller that happens to
         * be last out personally opened the socket is irrelevant — what matters is whether a waiter opened it
         * at all, because otherwise the non-creating waiter bailed here and wedged the socket in the field.
         *
         * An already-finished socket is abandoned regardless of provenance: nothing can still be depending on
         * it, and dropping it is the only way out of the state mainline's onclose leaves behind, where
         * connect() returns early forever because the field is still populated.
         */
        const openedByAWaiter = this.socketOpenedByWaiter === ws;
        const finished = ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING;
        if (!openedByAWaiter && !finished) {
            return;
        }
        this.socket = undefined;
        this.connected = false;
        this.isConnecting = false;
        if (this.socketOpenedByWaiter === ws) {
            this.socketOpenedByWaiter = undefined;
        }
        ws.close(this.internalConnectionCloseCode);
    }

    /**
     * Adds a listener associated to an event key
     * @param eventKey The server side event key to listen to
     * @param handler The function that get executed when the server send a message matching the eventKey
     * @param extraArgs Optional arguments to be passed when invoking the handler
     */
    addKeyListener(eventKey:string, handler: (data:any, extraArgs?: any[]) => void, extraArgs?: any[]) {
        this.listeners.push({key: eventKey, handler, extraArgs});
    }

    /**
     * Adds a listener with no event key filter
     * @param handler The function that get executed when the server send a message
     * @param extraArgs Optional arguments to be passed when invoking the handler
     */
    addListener(handler: (data:any, extraArgs?: any[]) => void, extraArgs?: any[]) {
        this.listeners.push({key: '', handler, extraArgs});
    }

    /**
     * Sends a message to the server, if the connection is not open it adds the message to a temporary list
     * @param action The server side action name for this message
     * @param message The message payload
     * @returns true if the message was handed to an open socket, false if it was queued for the next open
     *
     * The return value is additive: the condition above is unchanged, and every existing caller ignores the
     * result, so nothing about their behaviour moves. It exists because queueing is indistinguishable from
     * sending to a caller that ignores it, and a bulk promotion queued that way left the page waiting on a
     * verdict for a batch the server had never been told about.
     *
     * <p>NOTE, and it is the reason this cannot be relied on as proof of delivery: `connected` is only cleared
     * in onclose/onerror while the browser moves readyState to CLOSED *before* dispatching close, so in that
     * window this reports true for a frame send() discarded. Testing readyState here instead would change
     * behaviour for all ten existing consumers, so it is deliberately left alone and tracked separately.
     */
    sendMessage(action: string, message:any): boolean {
        message.action = action;
        if (!this.connected || this.socket?.readyState === WebSocket.CONNECTING){
            this.pendingMessages.push(message);
            console.log("Message could not be sent. Added as pending message: ",message);
            return false;
        }
        this._sendMessage(message);
        return true;
    }

    /**
     * Removes a listener from the list
     * @param eventKey The event key associated to the listener
     * @param handler The added function used to find the right listener
     */
    removeListener(eventKey:string, handler: (args:any) => void) {
        const foundIndex = this.listeners.findIndex(x => x.key === eventKey && x.handler == handler);
        this.listeners.splice(foundIndex, 1);
    }

    /**
     * Called when the client explicitly want to disconnect from the server
     * Note: Listeners are cleared, so if re-connected no listener will be triggered
     */
    disconnect(){
        this.listeners = [];
        this.connected = false;
    }

    private _sendMessage(message:any){
        this.socket?.send(JSON.stringify(message));
        this.listeners.forEach(l => {
            if (l.key == WebSocketApi.ConnectionEvent.ON_MESSAGE_SENT){
                // Notify listerners
                l.handler(message, l.extraArgs);
            }
        });
    }
};